// ✅ src/pages/VehicleV2/ProductionandDieselConsReport_Print.jsx
// ✅ Print template like your screenshot (Nissi header + bordered blocks + table)
// ✅ Reads filters + meta (pageSize/pageNo/totalRecords) from URL
// ✅ Calls: GET http://nissi-india.com/api/productionDieselConsReport/print?... (backend print API)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/productionDieselConsReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (val) => {
  const s = toStr(val);
  if (!s) return "";

  // already dd/MM/yyyy
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  // try parse
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const toNumStr = (v, digits = 2) => {
  const n = Number(String(v ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return toStr(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

// normalize print row => EXACT columns in print table
function normalizeRow(r) {
  return {
    date: fmtDate(r?.fld_progressentrydate ?? r?.date ?? r?.Date),
    nooftrips: toNumStr(r?.nooftrips ?? r?.NoofTrips ?? r?.trips ?? 0, 0),
    qtytotal: toNumStr(r?.qtytotal ?? r?.TotalQty ?? r?.totalQty ?? 0, 2),
    qtycumulative: toNumStr(r?.qtycumulative ?? r?.CumQty ?? r?.cumQty ?? 0, 2),
    tonnespertrip: toNumStr(r?.tonnespertrip ?? r?.TonnesPerTrip ?? r?.tonnesPerTrip ?? 0, 2),
    dieselconsumedltr: toNumStr(r?.dieselconsumedltr ?? r?.DieselConsumed ?? r?.dieselConsumed ?? 0, 2),
    dieselconsumedpertrip: toNumStr(r?.dieselconsumedpertrip ?? r?.DieselConsPerTrip ?? r?.dieselConsPerTrip ?? 0, 2),
    dieselconsumedtonnes: toNumStr(r?.dieselconsumedtonnes ?? r?.DieselConsPerTonnes ?? r?.dieselConsPerTonnes ?? 0, 2),
  };
}

export default function ProductionandDieselConsReport_Print() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  // ✅ read filters + meta from query string
  const q = useMemo(() => {
    const get = (k, d = "") => toStr(searchParams.get(k) ?? d);

    return {
      branchType: get("branchType", "HO"),
      branch: get("branch", ""),
      fromDate: get("fromDate", ""),
      toDate: get("toDate", ""),
      vehtype: get("vehtype", "O"),
      vehCode: get("vehCode", ""),
      vehName: get("vehName", ""),
      vehRegNo: get("vehRegNo", ""),

      // meta
      pageSize: Number(get("pageSize", "0")) || 0,
      pageNo: Number(get("pageNo", "1")) || 1,
      totalRecords: Number(get("totalRecords", "0")) || 0,
    };
  }, [searchParams]);

  // ✅ build backend print API URL
  const printUrl = useMemo(() => {
    const p = new URLSearchParams();

    // send only filters your backend expects
    p.set("branchType", q.branchType || "HO");
    p.set("branch", q.branch || "");
    p.set("fromDate", q.fromDate || "");
    p.set("toDate", q.toDate || "");
    p.set("vehtype", q.vehtype || "O");
    p.set("vehCode", q.vehCode || "");
    p.set("vehName", q.vehName || "");
    p.set("vehRegNo", q.vehRegNo || "");

    return `${API_BASE}/list?${p.toString()}`;
  }, [q]);

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(printUrl);
        if (!res?.data?.success) throw new Error(res?.data?.message || "Failed to load print data");

        const raw = res?.data?.rows || [];
        if (!alive) return;

        setRows(raw);
      } catch (e) {
        if (!alive) return;
        setRows([]);
        setErr(e?.response?.data?.message || e.message || "Failed to load print data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [printUrl]);

  const normalized = useMemo(() => rows.map(normalizeRow), [rows]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      <style>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 8pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; font-size: 7pt; width: 100%; }
          th, td { border: 1px solid #000 !important; padding: 2px !important; }
        }
        @page { size: A4 portrait; margin: 0.3in; }
      `}</style>

      <div className="p-2">
        {/* Actions */}
        <div className="no-print flex items-center justify-between mb-3">
          <button onClick={() => navigate(-1)} className="px-4 py-2 border rounded hover:bg-gray-50">
            ← Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
          >
            🖨️ Print
          </button>
        </div>

        {/* Loading/Error */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">Loading print data...</div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">{err}</div>
        ) : null}

        {/* Header */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            <div className="w-20 h-16 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold">Nissi Infotech</h1>
              <div className="text-sm font-semibold">Nissi</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
              </div>
              <div className="text-xs">
                Phone No : 9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infracnsoft-implementation@nissiinfotech.in
              </div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <h2 className="text-base font-bold">Production Diesel Consumption</h2>
        </div>

        {/* From/To + Meta */}
        <div className="mb-2 border-2 border-black p-2 text-xs">
          <div className="flex flex-wrap gap-x-10 gap-y-2">
            <div>
              <span className="font-bold mr-2">From Date</span>: {fmtDate(q.fromDate)}
            </div>
            <div>
              <span className="font-bold mr-2">To Date</span>: {fmtDate(q.toDate)}
            </div>

            {/* ✅ meta passed from report page */}
            <div>
              <span className="font-bold mr-2">Page No</span>: {q.pageNo || 1}
            </div>
            <div>
              <span className="font-bold mr-2">Page Size</span>: {q.pageSize || 0}
            </div>
            <div>
              <span className="font-bold mr-2">Total Records</span>: {q.totalRecords || 0}
            </div>
            <div>
              <span className="font-bold mr-2">Printed Records</span>: {normalized.length}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-1 py-1 font-bold text-center">S.No</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Date</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Noof Trips</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Total Qty</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Cum Qty</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Tonnes Per Trip</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Consumed(Ltr)</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cons Per Trip</th>
                <th className="border border-black px-1 py-1 font-bold text-center">Diesel Cons Per Tonnes</th>
              </tr>
            </thead>

            <tbody>
              {normalized.length === 0 ? (
                <tr>
                  <td colSpan={9} className="border border-black px-2 py-2 text-center">
                    No Records Found
                  </td>
                </tr>
              ) : (
                normalized.map((r, idx) => (
                  <tr key={`${r.date}-${idx}`} className="bg-white">
                    <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.date}</td>
                    <td className="border border-black px-1 py-1 text-center">{r.nooftrips}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.qtytotal}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.qtycumulative}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.tonnespertrip}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.dieselconsumedltr}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.dieselconsumedpertrip}</td>
                    <td className="border border-black px-1 py-1 text-right">{r.dieselconsumedtonnes}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          <div className="flex items-center justify-between">
            <p>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</p>
            <p className="font-bold">INFRA SCREEN : Production Diesel Consumption</p>
          </div>
        </div>
      </div>
    </div>
  );
}
